// src/newDashboard/admin/AdminDemo.jsx
//
// Admin "Demo bar" page: a pretend bar ("BFF Demo Bar", Oakdale MN) for
// making videos and testing. Only admins and the accounts listed here can
// see it in the app. See functions/src/demoBar.ts.

import React, { useEffect, useState } from "react";
import { auth } from "../../firebase";

const URL = "https://us-central1-bar-buzz-a6645.cloudfunctions.net/demoBar";

async function post(body) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

const box = { background: "#17131F", borderRadius: "14px", padding: "18px", marginBottom: "14px" };
const muted = { color: "#B8B0C8", fontSize: "15px", lineHeight: 1.5 };
const btn = {
  minHeight: "44px", padding: "0 16px", borderRadius: "10px", border: "none",
  font: "700 15px Inter, sans-serif", cursor: "pointer", marginRight: "8px",
};
const pink = { ...btn, background: "#FF3D81", color: "#0B0B12" };
const gray = { ...btn, background: "#3B2F5C", color: "#fff" };
const input = {
  minHeight: "44px", padding: "0 12px", borderRadius: "10px", border: "1px solid #3A3350",
  background: "#0D0B1A", color: "#fff", fontSize: "15px", minWidth: "260px", boxSizing: "border-box",
};

export default function AdminDemo() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [viewerEmail, setViewerEmail] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [charityEmail, setCharityEmail] = useState("");

  const run = async (body, okMsg) => {
    setBusy(true);
    setError("");
    setMsg("");
    try {
      setInfo(await post(body));
      if (okMsg) setMsg(okMsg);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  useEffect(() => {
    run({ action: "status" });
  }, []);

  return (
    <div style={{ color: "#fff", maxWidth: "820px" }}>
      <h2 style={{ marginBottom: "6px" }}>Demo bar</h2>
      <p style={{ ...muted, marginBottom: "16px" }}>
        A pretend bar for making videos and testing: <strong>BFF Demo Bar</strong> in Oakdale, MN, with sample
        specials, events (bingo, trivia, karaoke, meat raffle…) and amenities. Only admins and the accounts you add
        below can see it in the app. Real customers never see it, and it can't send texts.
      </p>

      {error && <div style={{ ...box, background: "#3A1420", color: "#FFC2D1" }}>{error}</div>}
      {msg && <div style={{ ...box, background: "#12301F", color: "#4ADE80" }}>{msg}</div>}

      <div style={box}>
        <h3 style={{ margin: "0 0 8px" }}>1. Make the demo bar</h3>
        {info?.exists ? (
          <p style={muted}>
            ✓ <strong style={{ color: "#fff" }}>{info.name}</strong> is set up with {info.events} sample events.
            Messed it up while testing? Reset it to put the sample specials and events back. Its photos and owner stay.
          </p>
        ) : (
          <p style={muted}>Not made yet. Click below to make it.</p>
        )}
        <button
          style={pink}
          disabled={busy}
          onClick={() => {
            if (info?.exists && !window.confirm("Put the demo bar's specials and events back to the samples? Changes you made to them will be replaced.")) return;
            run({ action: "reset" }, info?.exists ? "Demo bar reset." : "Demo bar made.");
          }}
        >
          {busy ? "One moment…" : info?.exists ? "Reset demo bar" : "Make demo bar"}
        </button>
      </div>

      <div style={box}>
        <h3 style={{ margin: "0 0 8px" }}>2. Who can see it in the app</h3>
        <p style={muted}>
          Admins always can. Add any other account you use for testing or videos (it must have signed in to the app
          once). After adding, sign out of the app and back in.
        </p>
        {info?.viewers?.length ? (
          <ul style={{ ...muted, margin: "8px 0 12px" }}>
            {info.viewers.map((v) => (
              <li key={v} style={{ marginBottom: "4px" }}>
                {v}{" "}
                <button
                  style={{ ...gray, minHeight: "32px", padding: "0 10px", fontSize: "13px" }}
                  disabled={busy}
                  onClick={() => run({ action: "removeViewer", email: v }, `${v} can't see the demo bar anymore.`)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ ...muted, margin: "8px 0 12px" }}>No extra accounts yet.</p>
        )}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <input style={input} placeholder="email of a test account" value={viewerEmail} onChange={(e) => setViewerEmail(e.target.value)} />
          <button
            style={gray}
            disabled={busy || !viewerEmail.trim()}
            onClick={() => run({ action: "addViewer", email: viewerEmail }, `${viewerEmail} can now see the demo bar.`).then(() => setViewerEmail(""))}
          >
            Add
          </button>
        </div>
      </div>

      <div style={box}>
        <h3 style={{ margin: "0 0 8px" }}>3. Manage it like a bar owner (optional)</h3>
        <p style={muted}>
          To film or test the owner dashboard, sign up at portal.barfunfinder.com as an <strong>Owner</strong> with a
          separate email (tip: <em>barfunfinder+demo@gmail.com</em> goes to your same Gmail inbox). Then enter that
          email here. That account will own the demo bar and can see it in the app.
        </p>
        <p style={{ ...muted, margin: "6px 0 10px" }}>
          Current owner: <strong style={{ color: "#fff" }}>{info?.ownerEmail || "none"}</strong>
        </p>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <input style={input} placeholder="owner account email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} />
          <button
            style={gray}
            disabled={busy || !ownerEmail.trim() || !info?.exists}
            onClick={() => run({ action: "setOwner", email: ownerEmail }, `${ownerEmail} now owns the demo bar.`).then(() => setOwnerEmail(""))}
          >
            Make owner
          </button>
        </div>
      </div>

      <div style={box}>
        <h3 style={{ margin: "0 0 8px" }}>4. Manage it like a charity (optional)</h3>
        <p style={muted}>
          To film or test the charity dashboard (gaming, bingo and raffle events, pull-tab posts), sign up at
          portal.barfunfinder.com as a <strong>Charity</strong> with another separate email (tip:{" "}
          <em>barfunfinder+charity@gmail.com</em>). Then enter that email here.
        </p>
        <p style={{ ...muted, margin: "6px 0 10px" }}>
          Current charity: <strong style={{ color: "#fff" }}>{info?.charityEmail || "none"}</strong>
        </p>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <input style={input} placeholder="charity account email" value={charityEmail} onChange={(e) => setCharityEmail(e.target.value)} />
          <button
            style={gray}
            disabled={busy || !charityEmail.trim() || !info?.exists}
            onClick={() => run({ action: "setCharity", email: charityEmail }, `${charityEmail} is now the demo bar's charity.`).then(() => setCharityEmail(""))}
          >
            Link charity
          </button>
        </div>
      </div>
    </div>
  );
}
